import React, { useEffect, useState } from "react";
import { CellSubType, CellType } from "utils/enums";
import { IProductTemplateAttribute } from "components/dashboard/product-template-table/utils/interfaces";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { toInstant } from "utils/time-utils";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { T } from "services/lang/LanguageService";
import { Instant } from "@js-joda/core";
import { useGetProductTemplateConfig } from "api/api-hooks/product-templates-hooks";
import { InputText } from "primereact/inputtext";
import { IProductTemplateDropdownChangeEvent } from "../product-template/ProductTemplateDropdown";

interface IHiddenColumnsDropdownProps {
  value?: Array<string>;
  onChange?: (e: MultiSelectChangeEvent | DropdownChangeEvent) => void;
  selectedTemplate?: IProductTemplateDropdownChangeEvent;
  className?: string;
  id?: string;
  disabled?: boolean;
  showClear?: boolean;
  setProductTemplateCreationDate?: (date: Instant) => void;
  setSelections?: (selections: Attribute[]) => void;
  allowedTypes?: CellType[];
  allowedSubtypes?: CellSubType[];
  singleSelect?: boolean;
}

export interface Attribute {
  label: string;
  type: CellType;
  value: string;
}

export default function ProductTemplateColumnsDropdown(props: IHiddenColumnsDropdownProps) {
  const [allSelections, setAllSelections] = useState<Attribute[]>([]);
  const query = useGetProductTemplateConfig(
    props.selectedTemplate?.refName!,
    `${props.selectedTemplate?.value}`,
    [],
    [],
    {
      enabled: !!props.selectedTemplate?.refName && !Number.isNaN(props.selectedTemplate?.value),
    }
  );

  useEffect(() => {
    if (query.data) {
      let selections = query.data.data?.attributes.map((element: IProductTemplateAttribute) => ({
        label: element.displayName,
        value: element.referenceName,
        type: element.type,
        subType: element.subType,
      }));

      if (props.allowedTypes) {
        selections = selections?.filter(selection => props.allowedTypes!.includes(selection.type));
      }

      if (props.allowedSubtypes && selections?.find(selection => CellType.MULTI_VALUE === selection.type)) {
        selections = selections?.filter(selection => {
          return props.allowedSubtypes!.includes(selection.subType as CellSubType);
        });
      }

      if (props.setProductTemplateCreationDate && query.data.dateCreated) {
        props.setProductTemplateCreationDate(toInstant(query.data.dateCreated));
      }

      setAllSelections(selections ?? []);
      props.setSelections?.(selections ?? []);
    }
  }, [query.data, props.allowedTypes]);

  const onChange = (e: MultiSelectChangeEvent) => {
    props.onChange?.(e);
  };

  const onDropdownChange = (e: DropdownChangeEvent) => {
    props.onChange?.(e);
  };

  if (!props.selectedTemplate?.refName || Number.isNaN(props.selectedTemplate?.value)) {
    return <InputText id={props.id} placeholder={T("message.product-template-is-not-selected")} disabled />;
  }
  return (
    <>
      {query.error && (
        <InputText id={props.id} placeholder={T("error.product-template-selection-error")} disabled />
      )}
      {query.isLoading && !query.error && (
        <InputText id={props.id} placeholder={T("message.product-templates-loading")} disabled />
      )}
      {query.isSuccess && (
        <div className="w-full">
          {props.singleSelect ? (
            <Dropdown
              id={props.id}
              className={`${props.className} w-full`}
              placeholder={T("action.select-column")}
              options={allSelections}
              value={props.value?.[0]}
              onChange={onDropdownChange}
              optionLabel="label"
              optionValue="value"
              showClear={props.showClear}
              disabled={props.disabled}
            />
          ) : (
            <MultiSelect
              placeholder={T("action.select-columns")}
              id={props.id}
              className={`${props.className} w-full`}
              value={props.value || []}
              options={allSelections}
              onChange={onChange}
              showClear={props.showClear}
              disabled={props.disabled}
              maxSelectedLabels={1}
            />
          )}
        </div>
      )}
    </>
  );
}
